import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";
import { formControlClass } from "../FormControls";

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 500,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: number;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-[2px] animate-fadeIn"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-[90%] max-h-[90vh] overflow-y-auto bg-card border border-border2 rounded-lg shadow-card scroll-thin"
        style={{ maxWidth }}
      >
        <div className="flex items-center justify-between px-5 py-5 border-b border-border">
          <h2 className="font-display text-base font-bold text-text">{title}</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center h-7 w-7 rounded-sm text-text2 hover:bg-bg4 hover:text-text transition-colors"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2.5 px-6 py-4 border-t border-border">{footer}</div>
        )}
      </div>
    </div>
  );
}

export function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-4">
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-text2 mb-1.5">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-[11px] text-danger">{error}</p>}
    </div>
  );
}

export const fieldInputClass = formControlClass;
